/**
 * Runtime config utilities: merge defaults + overrides and fetch remote flags.
 */
import defaults from "../config/defaults.json";
import flags from "../config/flags.json";
import { sanitizeAddonAllowlist } from "../lib/platform";
import type {
  IdentityCacheStorageSource,
  IdentityIndexedDbSource,
  PixelConfig,
  PixelFlags,
} from "../lib/types";
import { toBoolean, toNumber } from "../lib/utils";

function isObject(value: unknown): value is Record<string, unknown> {
  return Boolean(value) && typeof value === "object" && !Array.isArray(value);
}

function hasNoControlCharacters(value: string): boolean {
  for (const character of value) {
    const codePoint = character.charCodeAt(0);
    if (codePoint <= 31 || codePoint === 127) return false;
  }
  return true;
}

function isDataCategoryMode(
  value: unknown,
): value is "off" | "always" | "consented" {
  return value === "off" || value === "always" || value === "consented";
}

function sanitizeDataCategories(
  value: unknown,
): PixelConfig["privacy"]["dataCategories"] {
  if (!isObject(value)) return undefined;
  const output: Record<string, "off" | "always" | "consented"> = {};
  for (const [key, entry] of Object.entries(value)) {
    if (!isDataCategoryMode(entry)) continue;
    output[key] = entry;
  }
  return Object.keys(output).length > 0
    ? (output as PixelConfig["privacy"]["dataCategories"])
    : undefined;
}

function sanitizeRegionDataCategories(
  value: unknown,
): PixelConfig["privacy"]["regionDataCategories"] {
  if (!isObject(value)) return undefined;
  const output: Record<
    string,
    Partial<Record<string, "off" | "always" | "consented">>
  > = {};
  for (const [region, categories] of Object.entries(value)) {
    const sanitized = sanitizeDataCategories(categories) as
      | Partial<Record<string, "off" | "always" | "consented">>
      | undefined;
    if (!sanitized || Object.keys(sanitized).length === 0) continue;
    output[region] = sanitized;
  }
  return Object.keys(output).length > 0
    ? (output as PixelConfig["privacy"]["regionDataCategories"])
    : undefined;
}

function sanitizeConsentUi(
  value: unknown,
): PixelConfig["privacy"]["consentUi"] {
  if (!isObject(value)) return undefined;
  const mode =
    value.mode === "auto" || value.mode === "builtin" || value.mode === "host"
      ? value.mode
      : "auto";
  return {
    mode,
    privacyUrl:
      typeof value.privacyUrl === "string" ? value.privacyUrl.trim() : "",
    showPreferencesButton:
      value.showPreferencesButton === undefined
        ? true
        : toBoolean(value.showPreferencesButton),
  };
}

function sanitizeEventWeights(value: unknown) {
  if (!isObject(value)) return {};
  const output: Record<string, number> = {};
  for (const [eventType, rawWeight] of Object.entries(value).slice(0, 64)) {
    if (!/^[a-z0-9][a-z0-9_.:-]{0,63}$/i.test(eventType)) continue;
    const weight = Number(rawWeight);
    if (!Number.isFinite(weight)) continue;
    output[eventType] = Math.min(Math.max(weight, -100), 100);
  }
  return output;
}

function sanitizeVersion(value: unknown, fallback: string) {
  if (typeof value !== "string") return fallback;
  const normalized = value.trim();
  if (!/^[a-z0-9][a-z0-9_.:-]{0,79}$/i.test(normalized)) return fallback;
  return normalized;
}

function sanitizeIdentitySourceNames(value: unknown): string[] {
  if (!Array.isArray(value)) return [];
  return Array.from(
    new Set(
      value
        .filter((entry): entry is string => typeof entry === "string")
        .map((entry) => entry.trim())
        .filter(
          (entry) =>
            entry.length > 0 &&
            entry.length <= 128 &&
            hasNoControlCharacters(entry),
        ),
    ),
  ).slice(0, 32);
}

function sanitizeIndexedDbSources(value: unknown): IdentityIndexedDbSource[] {
  if (!Array.isArray(value)) return [];
  const output: IdentityIndexedDbSource[] = [];
  for (const entry of value.slice(0, 16)) {
    if (!isObject(entry)) continue;
    const database =
      typeof entry.database === "string" ? entry.database.trim() : "";
    const store = typeof entry.store === "string" ? entry.store.trim() : "";
    const key = entry.key;
    if (
      !database ||
      database.length > 128 ||
      !hasNoControlCharacters(database) ||
      !store ||
      store.length > 128 ||
      !hasNoControlCharacters(store)
    ) {
      continue;
    }
    if (
      !(
        (typeof key === "string" && key.length > 0 && key.length <= 256) ||
        (typeof key === "number" && Number.isFinite(key))
      )
    ) {
      continue;
    }
    output.push({ database, store, key: key as string | number });
  }
  return output;
}

function sanitizeCacheStorageSources(
  value: unknown,
): IdentityCacheStorageSource[] {
  if (!Array.isArray(value)) return [];
  const output: IdentityCacheStorageSource[] = [];
  for (const entry of value.slice(0, 16)) {
    if (!isObject(entry)) continue;
    const cacheName =
      typeof entry.cacheName === "string" ? entry.cacheName.trim() : "";
    const requestUrl =
      typeof entry.requestUrl === "string" ? entry.requestUrl.trim() : "";
    if (
      !cacheName ||
      cacheName.length > 128 ||
      !requestUrl ||
      requestUrl.length > 512 ||
      !hasNoControlCharacters(cacheName) ||
      !hasNoControlCharacters(requestUrl)
    ) {
      continue;
    }
    output.push({ cacheName, requestUrl });
  }
  return output;
}

export function deepMerge<T extends Record<string, unknown>>(
  base: T,
  override: Record<string, unknown>,
): T {
  const output = { ...base } as Record<string, unknown>;
  for (const [key, value] of Object.entries(override)) {
    if (isObject(value) && isObject(output[key])) {
      output[key] = deepMerge(output[key] as Record<string, unknown>, value);
    } else {
      output[key] = value;
    }
  }
  return output as T;
}

export async function loadJson<T>(url: string, fallback: T): Promise<T> {
  try {
    const response = await fetch(url, { cache: "no-store" });
    if (!response.ok) return fallback;
    return (await response.json()) as T;
  } catch {
    return fallback;
  }
}

export function resolveConfig(
  scriptUrl: URL,
  override?: Partial<PixelConfig>,
): PixelConfig {
  const winConfig =
    typeof window !== "undefined" && (window as any).PROSPIX_CONFIG
      ? (window as any).PROSPIX_CONFIG
      : {};

  const overrides: Record<string, unknown> = {
    ...winConfig,
    ...(override ?? {}),
  };

  const apiParam = scriptUrl.searchParams.get("api");
  const debugParam = scriptUrl.searchParams.get("debug");
  const siteKeyParam =
    scriptUrl.searchParams.get("siteKey") ||
    scriptUrl.searchParams.get("label") ||
    "";
  const consentMode = scriptUrl.searchParams.get("consentMode");
  const consentUi = scriptUrl.searchParams.get("consentUi");
  const privacyUrl = scriptUrl.searchParams.get("privacyUrl");
  const pluginsParam = scriptUrl.searchParams.get("plugins");

  if (apiParam) overrides.apiBase = apiParam;
  if (debugParam) overrides.debug = debugParam === "1";
  if (siteKeyParam) overrides.siteKey = siteKeyParam;
  if (consentMode) overrides.consentMode = consentMode;
  if (consentUi || privacyUrl) {
    const privacy = isObject(overrides.privacy) ? overrides.privacy : {};
    const currentConsentUi = isObject(privacy.consentUi)
      ? privacy.consentUi
      : {};
    overrides.privacy = {
      ...privacy,
      consentUi: {
        ...currentConsentUi,
        ...(consentUi ? { mode: consentUi } : {}),
        ...(privacyUrl ? { privacyUrl } : {}),
      },
    };
  }
  if (pluginsParam) {
    const enabled = pluginsParam
      .split(",")
      .map((value) => value.trim())
      .filter(Boolean);
    overrides.plugins = isObject(overrides.plugins)
      ? { ...overrides.plugins, enabled }
      : { enabled };
  }

  const merged = deepMerge(defaults as PixelConfig, overrides);

  const apiBase = merged.apiBase || scriptUrl.origin;
  const normalizedApi = (() => {
    try {
      return new URL(apiBase, scriptUrl.origin).origin;
    } catch {
      return scriptUrl.origin;
    }
  })();
  const defaultPlatform = (defaults as PixelConfig).platform;
  const platform = isObject(merged.platform)
    ? merged.platform
    : defaultPlatform;
  const platformAddons = isObject(platform.addons)
    ? platform.addons
    : defaultPlatform.addons;
  const defaultIntelligence = (defaults as PixelConfig).intelligence;
  const intelligence = isObject(merged.intelligence)
    ? merged.intelligence
    : defaultIntelligence;
  const intent = isObject(intelligence.intent)
    ? intelligence.intent
    : defaultIntelligence.intent;
  const journey = isObject(intelligence.journey)
    ? intelligence.journey
    : defaultIntelligence.journey;
  const warmThreshold = Math.min(
    Math.max(toNumber(intent.warmThreshold, 25), 1),
    99,
  );

  return {
    ...merged,
    apiBase: normalizedApi,
    debug: toBoolean(merged.debug),
    platform: {
      mode: platform.mode === "hybrid" ? "hybrid" : "first_party",
      addons: {
        enabled: sanitizeAddonAllowlist(platformAddons.enabled),
      },
    },
    privacy: {
      ...merged.privacy,
      consentUi: sanitizeConsentUi(merged.privacy?.consentUi),
      dataCategories: sanitizeDataCategories(merged.privacy?.dataCategories),
      regionDataCategories: sanitizeRegionDataCategories(
        merged.privacy?.regionDataCategories,
      ),
    },
    identityCapture: {
      enabled: toBoolean(merged.identityCapture?.enabled),
      automaticSources: toBoolean(merged.identityCapture?.automaticSources),
      markedForms: toBoolean(merged.identityCapture?.markedForms),
      endpointPath:
        typeof merged.identityCapture?.endpointPath === "string" &&
        /^\/[a-z0-9/_-]{1,120}$/i.test(merged.identityCapture.endpointPath)
          ? merged.identityCapture.endpointPath
          : "/pxl/identity",
      purposes: Array.isArray(merged.identityCapture?.purposes)
        ? merged.identityCapture.purposes.filter(
            (
              purpose,
            ): purpose is "identity_resolution" | "enrichment" | "marketing" =>
              purpose === "identity_resolution" ||
              purpose === "enrichment" ||
              purpose === "marketing",
          )
        : ["identity_resolution", "enrichment"],
      discovery: {
        cookieNames: sanitizeIdentitySourceNames(
          merged.identityCapture?.discovery?.cookieNames,
        ),
        localStorageKeys: sanitizeIdentitySourceNames(
          merged.identityCapture?.discovery?.localStorageKeys,
        ),
        sessionStorageKeys: sanitizeIdentitySourceNames(
          merged.identityCapture?.discovery?.sessionStorageKeys,
        ),
        indexedDb: sanitizeIndexedDbSources(
          merged.identityCapture?.discovery?.indexedDb,
        ),
        cacheStorage: sanitizeCacheStorageSources(
          merged.identityCapture?.discovery?.cacheStorage,
        ),
        maxCandidates: Math.min(
          Math.max(
            Math.round(
              toNumber(merged.identityCapture?.discovery?.maxCandidates, 10),
            ),
            1,
          ),
          20,
        ),
      },
    },
    queue: {
      ...merged.queue,
      flushIntervalMs: toNumber(merged.queue.flushIntervalMs, 2000),
      maxEvents: Math.min(toNumber(merged.queue.maxEvents, 100), 100),
      maxBytes: toNumber(merged.queue.maxBytes, 262144),
      ttlMs: toNumber(merged.queue.ttlMs, 86400000),
    },
    plugins: {
      ...merged.plugins,
      enabled: Array.isArray(merged.plugins.enabled)
        ? merged.plugins.enabled.filter((name) => typeof name === "string")
        : [],
      sampling: {
        rate: Math.min(
          Math.max(Number(merged.plugins.sampling.rate) || 1, 0),
          1,
        ),
      },
    },
    heatmap: {
      ...merged.heatmap,
      clickSampleRate: Math.min(
        Math.max(toNumber(merged.heatmap?.clickSampleRate, 0), 0),
        1,
      ),
      scrollSampleRate: Math.min(
        Math.max(toNumber(merged.heatmap?.scrollSampleRate, 0), 0),
        1,
      ),
      scrollThrottleMs: Math.max(
        toNumber(merged.heatmap?.scrollThrottleMs, 250),
        0,
      ),
    },
    video: {
      ...merged.video,
      progressMilestones: Array.isArray(merged.video?.progressMilestones)
        ? merged.video.progressMilestones
            .map((value) => Number(value))
            .filter((value) => Number.isFinite(value))
            .map((value) => Math.min(Math.max(value, 0), 100))
            .sort((a, b) => a - b)
        : [],
    },
    replay: {
      ...merged.replay,
      enabled: toBoolean(merged.replay?.enabled),
      chunkIntervalMs: Math.max(
        toNumber(merged.replay?.chunkIntervalMs, 2000),
        250,
      ),
      maxChunkBytes: Math.max(
        toNumber(merged.replay?.maxChunkBytes, 48000),
        1024,
      ),
    },
    intelligence: {
      intent: {
        ...intent,
        enabled: toBoolean(intent.enabled),
        modelVersion: sanitizeVersion(intent.modelVersion, "intent-v1"),
        windowMs: Math.min(
          Math.max(
            toNumber(intent.windowMs, 7 * 24 * 60 * 60 * 1000),
            5 * 60 * 1000,
          ),
          30 * 24 * 60 * 60 * 1000,
        ),
        emitMinIntervalMs: Math.min(
          Math.max(toNumber(intent.emitMinIntervalMs, 30_000), 5_000),
          10 * 60 * 1000,
        ),
        decayPerHour: Math.min(
          Math.max(toNumber(intent.decayPerHour, 1), 0),
          100,
        ),
        warmThreshold,
        hotThreshold: Math.min(
          Math.max(toNumber(intent.hotThreshold, 60), warmThreshold),
          100,
        ),
        maxReasons: Math.min(
          Math.max(Math.round(toNumber(intent.maxReasons, 5)), 1),
          10,
        ),
        weights: sanitizeEventWeights(intent.weights),
      },
      journey: {
        ...journey,
        enabled: toBoolean(journey.enabled),
        modelVersion: sanitizeVersion(journey.modelVersion, "journey-v1"),
        abandonmentAfterMs: Math.min(
          Math.max(
            toNumber(journey.abandonmentAfterMs, 30 * 60 * 1000),
            60 * 1000,
          ),
          24 * 60 * 60 * 1000,
        ),
      },
    },
  };
}

export async function loadFlags(scriptUrl: URL): Promise<PixelFlags> {
  const fallback = flags as PixelFlags;
  const url = new URL("/cdn/v1/config/flags.json", scriptUrl.origin);
  return loadJson(url.toString(), fallback);
}

export async function loadDefaults(scriptUrl: URL): Promise<PixelConfig> {
  const fallback = defaults as PixelConfig;
  const url = new URL("/cdn/v1/config/defaults.json", scriptUrl.origin);
  return loadJson(url.toString(), fallback);
}

export type RemotePixelConfig = {
  siteKey?: string;
  runtimeVersion?: string;
} & Partial<
  Pick<
    PixelConfig,
    | "features"
    | "platform"
    | "plugins"
    | "destinations"
    | "consentMode"
    | "privacy"
    | "identityCapture"
    | "intelligence"
  >
>;

export async function loadRemoteConfig(
  apiBase: string,
  siteKey: string,
): Promise<RemotePixelConfig | null> {
  if (!apiBase || !siteKey) return null;
  try {
    const url = new URL("/pxl/config", apiBase);
    url.searchParams.set("siteKey", siteKey);
    return loadJson<RemotePixelConfig | null>(url.toString(), null);
  } catch {
    return null;
  }
}
